import { memo, useRef, useState } from "react";
import useDeepCompareEffect from "../../hooks/useDeepCompareEffect";
import { DisaggregatedMetric, GeoDetails, GeoTag } from "../../services/types";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
    Chart,
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    Tooltip,
    Legend,
  } from 'chart.js';
import { formatValue } from "../../utils/helpers";

interface ComparisonChartProps {
    indicators: DisaggregatedMetric[] | null;
    metricId: number;
    countyData: DisaggregatedMetric | null;
    geoTag: GeoTag | null;
    geoDetails: GeoDetails[] | null;
    title: string;
    height: string;
}

const ComparisonChart = memo(({ indicators, metricId, countyData, geoTag, geoDetails, title, height }: ComparisonChartProps) => {
    const [data, setData] = useState<{ label: string; selected: number; county: number; rawSelected: number; rawCounty: number }[]>([]);
    const backgroundColor = '#125687';

    useDeepCompareEffect(() => {
        if (!indicators) return;

        // If geoTag is 06767 or geodetails contains a detail object with an id of 06767 then active selection is Sacramento County
        const isCountySelection =
            geoTag?.value === '06067' ||
            (!geoTag && geoDetails?.length === 0);

        // Fill selected area data with 0's if the current view is county
        const selectedAreaData = isCountySelection
        ? {
            id: 'placeholder',
            versions: [{
                disaggregations: countyData?.versions[0]?.disaggregations.map(d => ({
                    name: d.name,
                    value: 0,
                    percent_calc: {
                        n: 0,
                        d: 0
                    }
                })) || []
            }]
        }
        : indicators.find(data => data.id === metricId);
        
        if (!countyData) return;

        const selectedVersion = selectedAreaData ? selectedAreaData.versions[0] : null;
        const countyVersion = countyData.versions[0];

        if (!countyVersion) return;

        const tallyMap = new Map<string, { selected: number; county: number }>();

        selectedVersion?.disaggregations.forEach((d) => {
            tallyMap.set(d.name.trim(), { selected: d.value, county: 0 });
        });

        countyVersion.disaggregations.forEach((d) => {
            const entry = tallyMap.get(d.name.trim());
            if (entry) {
                entry.county = d.value;
            } else {
                tallyMap.set(d.name.trim(), { selected: 0, county: d.value });
            }
        });

        const allEntries = Array.from(tallyMap.entries());

        const totalSelected = allEntries.reduce((sum, [, values]) => sum + values.selected, 0);
        const totalCounty = allEntries.reduce((sum, [, values]) => sum + values.county, 0);

        const allValues = allEntries.map(([label, values]) => ({
            label,
            selected: totalSelected ? (values.selected / totalSelected) * 100 : 0,
            county: totalCounty ? (values.county / totalCounty) * 100 : 0,
            rawSelected: values.selected,
            rawCounty: values.county,
        }));

        setData(allValues);
    }, [indicators, countyData]);


    // Register the plugin to all charts:
    Chart.register(
        CategoryScale,
        LinearScale,
        BarElement,
        BarController,
        Tooltip,
        Legend,
        ChartDataLabels,
    );
    const chartRef = useRef<HTMLCanvasElement>(null);
    const chartInstance = useRef<Chart<"bar"> | null>(null);

    useDeepCompareEffect(() => {
        const ctx = chartRef.current?.getContext("2d");
        if (ctx) {
            // Destroy existing chart instance if it exists
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            // Extract labels and data
            const labels = data.map(d => d.label.replace(/ ?years?/, '').trim());
            const selectedAreaData = data.map(d => d.selected);
            const countyData = data.map(d => d.county);
            const minValue = Math.min(0, ...selectedAreaData, ...countyData);

            chartInstance.current = new Chart(ctx, {
                type: "bar",
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: "Selected Area",
                            data: selectedAreaData,
                            backgroundColor: "#6FAEDE",
                            hidden: selectedAreaData.every(value => value === 0),
                          },
                          {
                            label: "Sacramento County",
                            data: countyData,
                            backgroundColor: "#00A18A",
                          },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    indexAxis: 'x',
                    plugins: {
                        legend: {
                            display: true,
                            labels: {
                                filter: (legendItem, chartData) => {
                                    const datasetIndex = legendItem.datasetIndex;
                                    if (datasetIndex !== undefined && chartData.datasets[datasetIndex]) {
                                    const dataset = chartData.datasets[datasetIndex];
                                    return !dataset.hidden;
                                    }
                                    return false; // Hide the legend if datasetIndex is undefined
                                },
                                font: {
                                    size: 10,
                                },
                                boxWidth: 6,
                                boxHeight: 6,
                                usePointStyle: true,
                                pointStyle: 'circle',
                            },
                            position: 'bottom',
                        },
                        datalabels: {
                            display: false,
                        },
                        tooltip: {
                            callbacks: {
                                label: (tooltipItem) => {
                                    const datasetLabel = tooltipItem.dataset.label || 'Data';
                                    const label = tooltipItem.label || '';
                                    const value = Number(tooltipItem.raw);
                        
                                    const current = data.find(d => d.label.replace(/ ?years?/, '').trim() === label);
                                    if (!current) return '';
                        
                                    const raw =
                                        datasetLabel === "Selected Area"
                                            ? current.rawSelected
                                            : current.rawCounty;
                        
                                    return [
                                        `${datasetLabel}`,
                                        `Percent of Children ${label}: ${value.toFixed(2)}%`,
                                        `Number of Children ${label}: ${formatValue(raw, 'integer')}`,
                                    ];
                                },
                            },
                            bodyColor: '#FFFFFF',
                        },
                    },
                    layout: {
                        padding: {
                            left: 10,
                            right: 40,
                            top: 10,
                            bottom: 10,
                        }
                    },
                    scales: {
                        x: {
                            beginAtZero: true,
                            min: minValue,
                            // max: maxValue,
                            grid: {
                                display: false,
                                drawTicks: true,
                            },
                            border: {
                                display: false
                            },
                            ticks: {
                                minRotation: 0,
                                maxRotation: 0,
                                font: {
                                    size: 11,
                                    weight: 'bold',
                                },
                                padding: 0,
                                color: '#4D4D4D',
                                autoSkip: false,  // Ensure all labels are displayed
                            },
                        },
                        y: {
                            title: {
                                display: true,
                                text: 'Percent of Children',
                                font: {
                                    size: 11,
                                    weight: 'bold',
                                },
                                color: '#4D4D4D',
                                padding: {
                                    bottom: 10,
                                },
                            },
                            ticks: {
                                display: true,
                                color: "#4D4D4D",
                                minRotation: 0,
                                maxRotation: 0,
                                font: {
                                    size: 11,
                                    weight: 'bold',
                                },
                                padding: 10,
                                callback: function (value: string | number) {
                                    return `${value}%`;
                                },
                            },
                            grid: {
                                display: true,
                                color: '#D3D3D3',
                            },
                            border: {
                                display: false,
                                dash: [6, 4],
                            },
                        },
                    },
                },
                // plugins: [backgroundFillPlugin('#F0F0F0'), layoutPaddingPlugin],
            });
        }
    }, [data, title, backgroundColor]);

    return (
        <div className="w-full">
            <div className={height ? height : `h-80`}> {/* Adjust height as needed */}
                <canvas ref={chartRef} className="w-full bg-[#F0F0F0]"/>
            </div>
        </div>
    );
});

export default ComparisonChart;